import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  closeProductModal,
  refreshProductList,
} from "../../../redux/features/productModalSlice";
import { AddModule } from "../../../redux/services/moduleService/module.Services";
import Swal from "sweetalert2";
import { successToast } from "../../../Utils/toast";
 
const MAX_TODO = 5;
 
function CreateModule({ refreshData }) {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: 1,
    todo_list: [""],
  });
 
  // ---------- Handle simple field changes ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };
 
  // ---------- Handle todo_list item change ----------
  const handleTodoChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.todo_list];
      updated[index] = value;
      return { ...prev, todo_list: updated };
    });
 
    if (errors.todo_list) {
      setErrors((prev) => ({ ...prev, todo_list: null }));
    }
  };
 
  // ---------- Add new todo (max 5) ----------
  const addTodoItem = () => {
    if (formData.todo_list.length >= MAX_TODO) {
      Swal.fire(
        "Limit Reached",
        `You can add a maximum of ${MAX_TODO} todo items.`,
        "warning"
      );
      return;
    }
    setFormData((prev) => ({
      ...prev,
      todo_list: [...prev.todo_list, ""],
    }));
  };
 
  // ---------- Remove a todo ----------
  const removeTodoItem = (index) => {
    setFormData((prev) => {
      const updated = prev.todo_list.filter((_, i) => i !== index);
      return { ...prev, todo_list: updated.length ? updated : [""] };
    });
  };
 
  // ---------- Validation ----------
  const validateForm = () => {
    const newErrors = {};
 
    if (!formData.name.trim()) {
      newErrors.name = ["Name is required"];
    }
 
    const cleanedTodos = formData.todo_list
      .map((t) => t.trim())
      .filter((t) => t !== "");
 
    if (cleanedTodos.length > MAX_TODO) {
      newErrors.todo_list = [`Maximum ${MAX_TODO} todo items allowed`];
    }
 
    setErrors(newErrors);
    return newErrors;
  };
 
  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTimeout(() => {
        const firstErrorField = document.querySelector(".is-invalid");
        if (firstErrorField) {
          firstErrorField.focus();
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }
 
    // Clean todo_list: remove empty strings, limit to 5
    const cleanedTodoList = formData.todo_list
      .map((t) => t.trim())
      .filter((t) => t !== "")
      .slice(0, MAX_TODO);
 
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      status: formData.status,
      todo_list: cleanedTodoList,
    };
 
    try {
      const res = await AddModule(payload);
      if (res?.success) {
        successToast(res.message);
        refreshData?.();
        dispatch(refreshProductList());
        dispatch(closeProductModal());
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        Swal.fire("Error", "Something went wrong", "error");
      }
    }
  };
 
  return (
    <form onSubmit={handleSubmit}>
      {/* ---------- Name ---------- */}
      <div className="mb-3">
        <label>
          Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          name="name"
          className={`form-control ${errors?.name ? "is-invalid" : ""}`}
          placeholder="Module Name"
          value={formData.name}
          onChange={handleChange}
        />
        {errors?.name && (
          <div className="invalid-feedback d-block">{errors.name[0]}</div>
        )}
      </div>
 
      {/* ---------- Description ---------- */}
      <div className="mb-3">
        <label>Description</label>
        <textarea
          name="description"
          rows="3"
          className={`form-control ${
            errors?.description ? "is-invalid" : ""
          }`}
          placeholder="Module Description"
          value={formData.description}
          onChange={handleChange}
        />
        {errors?.description && (
          <div className="invalid-feedback d-block">
            {errors.description[0]}
          </div>
        )}
      </div>
 
      {/* ---------- Todo List ---------- */}
      <div className="mb-3">
        <label>
          Todo List{" "}
          <small className="text-muted">
            ({formData.todo_list.length}/{MAX_TODO})
          </small>
        </label>
 
        {formData.todo_list.map((todo, index) => (
          <div className="input-group mb-2" key={index}>
            <input
              type="text"
              className={`form-control ${
                errors?.todo_list ? "is-invalid" : ""
              }`}
              placeholder={`Task ${index + 1}`}
              value={todo}
              onChange={(e) => handleTodoChange(index, e.target.value)}
              maxLength={255}
            />
 
            {index === 0 ? (
              // ✅ First row → + Add button
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={addTodoItem}
                disabled={formData.todo_list.length >= MAX_TODO}
              >
                + Add
              </button>
            ) : (
              // ✅ Baaki rows → ✕ Close button
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeTodoItem(index)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
 
        {errors?.todo_list && (
          <div className="invalid-feedback d-block">
            {errors.todo_list[0]}
          </div>
        )}
      </div>
 
      {/* ---------- Submit ---------- */}
      <button className="add-btn" type="submit">
        Create Module
      </button>
    </form>
  );
}
 
export default CreateModule;